// WhatsApp message templates the sales team writes once and sends from the
// leads table. A template body may contain {{variables}}; the placeholders are
// written in Arabic so a non-technical commercial can compose one by hand.
//
// Shared by the template manager, the send popup and the server (which renders
// the same text into the audit trail).

import { joinAssigneeNames } from "./lead-assignees";
import { type LeadStatus, STATUS_LABELS } from "./lead-status";

/** The data a template can interpolate — a subset of a Lead row. */
export type TemplateLead = {
	fullName: string;
	phone: string;
	email?: string | null;
	wilaya?: string | null;
	jobStatus?: string | null;
	status: LeadStatus;
	createdAt: string | Date;
	assignees?: readonly { user: { name: string } }[] | null;
};

export type TemplateVariable = {
	/** The token as written inside {{ }} in a template body. */
	token: string;
	label: string;
	hint: string;
	resolve: (lead: TemplateLead, ctx: TemplateContext) => string;
};

export type TemplateContext = {
	/** The signed-in commercial, used by {{المرسل}}. */
	senderName?: string | null;
};

function formatDay(value: string | Date) {
	return new Intl.DateTimeFormat("ar-DZ", {
		dateStyle: "medium",
		timeZone: "Africa/Algiers",
	}).format(new Date(value));
}

export const TEMPLATE_VARIABLES: TemplateVariable[] = [
	{
		token: "الاسم",
		label: "الاسم الكامل",
		hint: "أحمد بن علي",
		resolve: (lead) => lead.fullName,
	},
	{
		token: "الاسم_الأول",
		label: "الاسم الأول",
		hint: "أحمد",
		resolve: (lead) => lead.fullName.trim().split(/\s+/)[0] ?? lead.fullName,
	},
	{
		token: "الهاتف",
		label: "رقم الهاتف",
		hint: "0551234567",
		resolve: (lead) => lead.phone,
	},
	{
		token: "البريد",
		label: "البريد الإلكتروني",
		hint: "name@mail.com",
		resolve: (lead) => lead.email ?? "",
	},
	{
		token: "الولاية",
		label: "الولاية",
		hint: "الجزائر",
		resolve: (lead) => lead.wilaya ?? "",
	},
	{
		token: "الوضعية",
		label: "الوضعية المهنية",
		hint: "موظف",
		resolve: (lead) => lead.jobStatus ?? "",
	},
	{
		token: "الحالة",
		label: "حالة الطلب",
		hint: "مؤكدة مع دفع",
		resolve: (lead) => STATUS_LABELS[lead.status] ?? lead.status,
	},
	{
		token: "تاريخ_التسجيل",
		label: "تاريخ التسجيل",
		hint: "12 مارس 2026",
		resolve: (lead) => formatDay(lead.createdAt),
	},
	{
		token: "المسؤول",
		label: "المسؤولون عن الطلب",
		hint: "سارة وأحمد",
		resolve: (lead) => joinAssigneeNames(lead.assignees),
	},
	{
		token: "المرسل",
		label: "المُرسِل (أنت)",
		hint: "اسمك كما هو في الحساب",
		resolve: (_lead, ctx) => ctx.senderName ?? "",
	},
];

const VARIABLE_BY_TOKEN = new Map(TEMPLATE_VARIABLES.map((v) => [v.token, v]));

// Matches {{ token }} with optional surrounding whitespace.
const PLACEHOLDER = /\{\{\s*([^{}]+?)\s*\}\}/g;

/**
 * Replaces every {{token}} with the lead's value. Unknown tokens are left as
 * written so a typo is visible in the preview rather than silently dropped.
 */
export function renderTemplate(
	body: string,
	lead: TemplateLead,
	ctx: TemplateContext = {},
): string {
	return body.replace(PLACEHOLDER, (whole, rawToken: string) => {
		const variable = VARIABLE_BY_TOKEN.get(rawToken.trim());
		if (!variable) return whole;
		return variable.resolve(lead, ctx);
	});
}

/** Tokens used in a body that don't exist — surfaced as a warning in the editor. */
export function unknownTokens(body: string): string[] {
	const found = new Set<string>();
	for (const match of body.matchAll(PLACEHOLDER)) {
		const token = match[1].trim();
		if (!VARIABLE_BY_TOKEN.has(token)) found.add(token);
	}
	return [...found];
}

/**
 * Algerian numbers are stored as the user typed them ("0551 23 45 67",
 * "+213 551…"). wa.me needs bare international digits.
 */
export function toWhatsAppNumber(phone: string) {
	const digits = phone.replace(/\D/g, "");
	if (digits.startsWith("213")) return digits;
	if (digits.startsWith("0")) return `213${digits.slice(1)}`;
	// 9-digit local number written without the leading zero.
	if (digits.length === 9) return `213${digits}`;
	return digits;
}

export function whatsAppLink(phone: string, message?: string) {
	const base = `https://wa.me/${toWhatsAppNumber(phone)}`;
	return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

/**
 * Seeded into an empty database the first time the manager is opened, so the
 * team starts from working examples instead of a blank slate.
 */
export const STARTER_TEMPLATES: Array<{
	name: string;
	category: string;
	body: string;
}> = [
	{
		name: "أول اتصال",
		category: "استقبال",
		body:
			"السلام عليكم {{الاسم_الأول}} 👋\n" +
			"معك {{المرسل}} من أكاديمية مستشير.\n" +
			"توصّلنا بطلب تسجيلك في الدورة، ونحبّ نأكّد معك المعلومات ونجاوبك على أي سؤال.\n" +
			"واش الوقت المناسب باش نتصلو بيك؟",
	},
	{
		name: "لم يرد على الاتصال",
		category: "متابعة",
		body:
			"السلام عليكم {{الاسم_الأول}}،\n" +
			"حاولنا نتصلو بيك من أكاديمية مستشير بخصوص طلب تسجيلك ولم نتمكّن من الوصول إليك.\n" +
			"إذا مازلت مهتم، ردّ علينا هنا وباش نكملو معاك التسجيل. 🙏",
	},
	{
		name: "تأكيد التسجيل",
		category: "تأكيد",
		body:
			"مبروك {{الاسم_الأول}} 🎉\n" +
			"تم تأكيد تسجيلك في الدورة بأكاديمية مستشير.\n" +
			"باش نبعثولك تفاصيل الانطلاق والرابط قبل الموعد بوقت كافٍ.\n" +
			"مرحباً بك معنا!",
	},
	{
		name: "تذكير بالدفع",
		category: "تأكيد",
		body:
			"السلام عليكم {{الاسم_الأول}}،\n" +
			"مكانك في الدورة محجوز مؤقتاً ✅\n" +
			"باش نثبّتوه نهائياً، يلزم استكمال الدفع.\n" +
			"إذا حبيت نشرحولك طرق الدفع المتاحة، قلّنا ونعاونوك.",
	},
];
